import { useEffect, useMemo, useRef, useState } from "react";
import PieChartCard   from "./PieChartCard";
import ModelPortfolio from "./ModelPortfolio";
import { useSymbols } from "./useSymbols";
import { usePrices } from "./usePrices";

const getCurrency = (cat) => cat === "BIST" ? "₺" : "$";

/* ─── Constants ─────────────────────────────────────────────── */
const CATEGORIES = ["BIST", "NASDAQ", "KRIPTO", "KMADEN"];

const CAT_CONFIG = {
  BIST:   { color: "#6366f1", icon: "🇹🇷" },
  NASDAQ: { color: "#06b6d4", icon: "🇺🇸" },
  KRIPTO: { color: "#f59e0b", icon: "₿"   },
  KMADEN: { color: "#10b981", icon: "🥇"  },
};

const PALETTE = [
  "#6366f1","#06b6d4","#f59e0b","#10b981",
  "#ec4899","#8b5cf6","#14b8a6","#f97316",
  "#3b82f6","#a855f7",
];

const fmtTRY = (n) =>
  n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUSD = (n) => {
  if (n >= 1000)  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n >= 1)     return n.toFixed(2);
  return n.toPrecision(4);
};

const priceFmt = (price, category) =>
  category === "BIST"
    ? `₺${fmtTRY(price)}`
    : `$${fmtUSD(price)}`;

/* ─── App ────────────────────────────────────────────────────── */
export default function App() {
  /* State */
  const [portfolio, setPortfolio] = useState(() => {
    try { return JSON.parse(localStorage.getItem("portfolio_ok") || "[]"); }
    catch { return []; }
  });
  const [form, setForm]       = useState({ symbol: "", amount: "", cost: "", category: "BIST" });
  const [dark, setDark]       = useState(() => localStorage.getItem("theme_ok") === "dark");
  const [activeTab, setTab]   = useState("portfolio");
  const [toast, setToast]     = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const sugRef = useRef(null);

  /* Symbols (autocomplete + category detection, Sheets'ten yüklenir) */
  const { filterAll, detectCat } = useSymbols();

  /* Prices */
  const { usdtry, loading: pricesLoading, lastUpdated,
          fetchPrices, fetchSinglePrice, getPrice } = usePrices();

  /* Persistence */
  useEffect(() => { localStorage.setItem("portfolio_ok", JSON.stringify(portfolio)); }, [portfolio]);
  useEffect(() => { localStorage.setItem("theme_ok", dark ? "dark" : "light"); }, [dark]);

  /* Auto-fetch prices on mount */
  useEffect(() => {
    if (portfolio.length > 0) fetchPrices(portfolio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Theme */
  const t = {
    bg:      dark ? "#0b1120" : "#f1f5f9",
    card:    dark ? "#1a2332" : "#ffffff",
    cardAlt: dark ? "#1e2c3d" : "#f8fafc",
    text:    dark ? "#f0f4f8" : "#0f172a",
    border:  dark ? "#2d3f55" : "#e2e8f0",
    inputBg: dark ? "#0b1120" : "#ffffff",
    muted:   dark ? "#7a8fa6" : "#64748b",
    danger:  "#ef4444",
    success: "#10b981",
  };

  /* Toast */
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2800); };

  /* ── Canvas pie chart share ── */
  const shareAsImage = (data, colors, label) => {
    const W    = 480;
    const R    = 170;
    const cx   = W / 2;
    const cy   = 290;
    const hole = R * 0.52;

    const legendRows = Math.ceil(data.length / 2);
    const H = cy + R + 36 + legendRows * 28 + 46;

    const canvas = document.createElement("canvas");
    canvas.width  = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    /* Background */
    ctx.fillStyle = dark ? "#0b1120" : "#f1f5f9";
    ctx.fillRect(0, 0, W, H);

    /* Title */
    ctx.textAlign    = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle    = dark ? "#f0f4f8" : "#0f172a";
    ctx.font         = "bold 18px system-ui, sans-serif";
    ctx.fillText(label, cx, 18);

    ctx.font      = "12px system-ui, sans-serif";
    ctx.fillStyle = dark ? "#7a8fa6" : "#64748b";
    ctx.fillText(new Date().toLocaleDateString("tr-TR"), cx, 44);

    /* Pie slices */
    let angle = -Math.PI / 2;
    data.forEach((d, i) => {
      const sweep = (d.percent / 100) * 2 * Math.PI;
      const end   = angle + sweep;
      const color = colors[i % colors.length];

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, angle, end);
      ctx.closePath();
      ctx.fillStyle   = color;
      ctx.fill();
      ctx.strokeStyle = dark ? "#0b1120" : "#f1f5f9";
      ctx.lineWidth   = 2;
      ctx.stroke();

      /* Slice label */
      if (d.percent >= 5) {
        const mid = angle + sweep / 2;
        const lr  = (hole + R) / 2;
        const lx  = cx + lr * Math.cos(mid);
        const ly  = cy + lr * Math.sin(mid);

        ctx.fillStyle    = "#fff";
        ctx.textAlign    = "center";
        ctx.textBaseline = "middle";
        ctx.font         = "bold 13px system-ui, sans-serif";
        ctx.fillText(`%${d.percent.toFixed(0)}`, lx, ly - 7);
        ctx.font      = "10px system-ui, sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillText(d.symbol, lx, ly + 7);
      }

      angle = end;
    });

    /* Donut hole */
    ctx.beginPath();
    ctx.arc(cx, cy, hole, 0, 2 * Math.PI);
    ctx.fillStyle = dark ? "#0b1120" : "#f1f5f9";
    ctx.fill();

    /* Center text */
    ctx.textAlign    = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle    = dark ? "#f0f4f8" : "#0f172a";
    ctx.font         = "bold 16px system-ui, sans-serif";
    ctx.fillText("OWL", cx, cy - 10);
    ctx.font      = "11px system-ui, sans-serif";
    ctx.fillStyle = dark ? "#7a8fa6" : "#64748b";
    ctx.fillText("Portfolio", cx, cy + 9);

    /* Legend */
    const legendTop = cy + R + 24;
    ctx.textAlign    = "left";
    ctx.textBaseline = "middle";
    data.forEach((d, i) => {
      const col    = i % 2;
      const row    = Math.floor(i / 2);
      const lx     = 36 + col * (W / 2 - 10);
      const ly     = legendTop + row * 28;
      const color  = colors[i % colors.length];

      ctx.beginPath();
      ctx.arc(lx + 6, ly, 6, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      ctx.fillStyle = dark ? "#f0f4f8" : "#0f172a";
      ctx.font      = "13px system-ui, sans-serif";
      ctx.fillText(`${d.symbol}  %${d.percent.toFixed(1)}`, lx + 18, ly);
    });

    /* Disclaimer */
    ctx.textAlign    = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle    = dark ? "#374151" : "#94a3b8";
    ctx.font         = "10px system-ui, sans-serif";
    ctx.fillText("⚠️ Yatırım tavsiyesi değildir · OWL Portfolio", cx, H - 10);

    /* Download / Share */
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "owl-portfolio.png", { type: "image/png" });
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: `OWL Portfolio · ${label}` });
        } else {
          const url = URL.createObjectURL(blob);
          const a   = document.createElement("a");
          a.href    = url;
          a.download = "owl-portfolio.png";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showToast("Görsel indirildi!");
        }
      } catch { showToast("Paylaşım iptal edildi"); }
    }, "image/png");
  };

  /* ── Share: tüm varlıklar yüzdelik bar grafiği ── */
  const shareAllAssets = () => {
    const rate  = usdtry || 1;
    const items = portfolio
      .map((p) => ({
        symbol:   p.symbol,
        category: p.category,
        value:    p.amount * p.cost * (p.category === "BIST" ? 1 : rate),
      }))
      .sort((a, b) => b.value - a.value);
    const total = items.reduce((s, a) => s + a.value, 0);
    if (total === 0) return;
    const data = items.map((a) => ({ ...a, percent: (a.value / total) * 100 }));

    const W      = 520;
    const ITEM_H = 44;
    const HEADER = 88;
    const FOOTER = 34;
    const H      = HEADER + data.length * ITEM_H + FOOTER;

    const canvas  = document.createElement("canvas");
    canvas.width  = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    /* BG */
    ctx.fillStyle = dark ? "#0b1120" : "#f1f5f9";
    ctx.fillRect(0, 0, W, H);

    /* Title */
    ctx.textAlign    = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle    = dark ? "#f0f4f8" : "#0f172a";
    ctx.font         = "bold 20px system-ui, sans-serif";
    ctx.fillText("🦉 OWL Portföy Dağılımı", 24, 14);

    ctx.font      = "12px system-ui, sans-serif";
    ctx.fillStyle = dark ? "#7a8fa6" : "#64748b";
    ctx.fillText(new Date().toLocaleDateString("tr-TR"), 24, 42);

    if (totalTRY != null) {
      ctx.textAlign = "right";
      ctx.fillStyle = dark ? "#f0f4f8" : "#0f172a";
      ctx.font      = "bold 14px system-ui, sans-serif";
      ctx.fillText(`₺${fmtTRY(totalTRY)}`, W - 24, 42);
      ctx.textAlign = "left";
    }

    /* Divider */
    ctx.strokeStyle = dark ? "#2d3f55" : "#e2e8f0";
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(24, HEADER - 10);
    ctx.lineTo(W - 24, HEADER - 10);
    ctx.stroke();

    const BAR_X    = 152;
    const BAR_MAXW = W - BAR_X - 68;

    data.forEach((item, i) => {
      const y   = HEADER + i * ITEM_H;
      const cfg = CAT_CONFIG[item.category] || { color: "#6366f1" };
      const barW = Math.max(4, (item.percent / 100) * BAR_MAXW);
      const midY = y + ITEM_H / 2;

      /* Symbol */
      ctx.textAlign    = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle    = dark ? "#f0f4f8" : "#0f172a";
      ctx.font         = "bold 13px system-ui, sans-serif";
      ctx.fillText(item.symbol, 24, midY);

      /* Category dot */
      ctx.beginPath();
      ctx.arc(BAR_X - 12, midY, 5, 0, 2 * Math.PI);
      ctx.fillStyle = cfg.color;
      ctx.fill();

      /* Bar bg */
      ctx.fillStyle = dark ? "#1a2332" : "#e2e8f0";
      ctx.fillRect(BAR_X, midY - 6, BAR_MAXW, 12);

      /* Bar fill */
      ctx.fillStyle = cfg.color;
      ctx.fillRect(BAR_X, midY - 6, barW, 12);

      /* Percentage */
      ctx.textAlign    = "right";
      ctx.textBaseline = "middle";
      ctx.fillStyle    = dark ? "#f0f4f8" : "#0f172a";
      ctx.font         = "bold 12px system-ui, sans-serif";
      ctx.fillText(`%${item.percent.toFixed(1)}`, W - 20, midY);

      /* Row divider */
      if (i < data.length - 1) {
        ctx.strokeStyle = dark ? "#1a233299" : "#f1f5f966";
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.moveTo(24, y + ITEM_H);
        ctx.lineTo(W - 24, y + ITEM_H);
        ctx.stroke();
      }
    });

    /* Footer */
    ctx.textAlign    = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle    = dark ? "#374151" : "#94a3b8";
    ctx.font         = "10px system-ui, sans-serif";
    ctx.fillText("⚠️ Yatırım tavsiyesi değildir · OWL Portfolio", W / 2, H - 8);

    canvas.toBlob(async (blob) => {
      const file = new File([blob], "owl-portfolio.png", { type: "image/png" });
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: "OWL Portföy Dağılımı" });
        } else {
          const url = URL.createObjectURL(blob);
          const a   = document.createElement("a");
          a.href    = url;
          a.download = "owl-portfolio.png";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showToast("Görsel indirildi!");
        }
      } catch { showToast("Paylaşım iptal edildi"); }
    }, "image/png");
  };

  /* ── Symbol autocomplete ── */
  const onSymbolChange = (val) => {
    setForm((f) => {
      const next = { ...f, symbol: val };
      const cat  = detectCat(val.trim().toUpperCase());
      if (cat) next.category = cat;
      return next;
    });
    const hits = filterAll(val);
    setSuggestions(hits);
    setShowSug(hits.length > 0 && val.trim().length > 0);
  };

  const pickSuggestion = (sym, cat) => {
    setShowSug(false);
    setForm((f) => ({ ...f, symbol: sym, category: cat, cost: "" }));
  };

  /* ── Add asset ── */
  const addAsset = async (e) => {
    e.preventDefault();
    const sym    = form.symbol.trim().toUpperCase();
    const amount = parseFloat(form.amount);
    const cost   = parseFloat(form.cost);
    if (!sym || isNaN(amount) || isNaN(cost) || amount <= 0 || cost <= 0) return;

    // Validate category vs known symbols
    const detectedCat = detectCat(sym);
    if (detectedCat && detectedCat !== form.category) {
      showToast(`⚠️ ${sym} sembolü ${detectedCat} kategorisine ait!`);
      setForm((f) => ({ ...f, category: detectedCat }));
      return;
    }

    setPortfolio((prev) => {
      const existing = prev.find((p) => p.symbol === sym && p.category === form.category);
      if (!existing) {
        return [...prev, { id: Date.now(), symbol: sym, amount, cost, category: form.category }];
      }
      const totalAmount = existing.amount + amount;
      const avgCost     = (existing.amount * existing.cost + amount * cost) / totalAmount;
      return prev.map((p) =>
        p.id === existing.id ? { ...p, amount: totalAmount, cost: avgCost } : p
      );
    });

    setForm((f) => ({ ...f, symbol: "", amount: "", cost: "" }));

    // Fetch price for this asset so K/Z shows immediately
    fetchSinglePrice(sym, form.category);
  };

  const removeAsset = (id) => setPortfolio((p) => p.filter((x) => x.id !== id));

  /* ── Derived data ── */
  const bistValue = useMemo(
    () => portfolio.filter((p) => p.category === "BIST").reduce((s, p) => s + p.amount * p.cost, 0),
    [portfolio]
  );
  const usdValue = useMemo(
    () => portfolio.filter((p) => p.category !== "BIST").reduce((s, p) => s + p.amount * p.cost, 0),
    [portfolio]
  );
  const totalTRY = usdtry != null ? bistValue + usdValue * usdtry : null;

  const categoryPie = useMemo(() => {
    // Use TRY-converted values for the overview pie
    const rate = usdtry || 1;
    const map  = {};
    portfolio.forEach((p) => {
      const v = p.amount * p.cost * (p.category === "BIST" ? 1 : rate);
      map[p.category] = (map[p.category] || 0) + v;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    if (total === 0) return [];
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([cat, value]) => ({ symbol: cat, value, percent: (value / total) * 100 }));
  }, [portfolio, usdtry]);

  const perCategoryPie = useMemo(() => {
    const result = {};
    CATEGORIES.forEach((cat) => {
      const items    = portfolio.filter((p) => p.category === cat);
      const catTotal = items.reduce((s, p) => s + p.amount * p.cost, 0);
      result[cat]    = items.map((p) => ({
        symbol:  p.symbol,
        value:   p.amount * p.cost,
        percent: catTotal > 0 ? (p.amount * p.cost / catTotal) * 100 : 0,
      }));
    });
    return result;
  }, [portfolio]);

  const activeCategories = CATEGORIES.filter((c) => portfolio.some((p) => p.category === c));

  const portfolioPnl = useMemo(() => {
    const rate = usdtry;
    let costTRY = 0, curTRY = 0, hasAny = false;
    portfolio.forEach((p) => {
      const cur = getPrice(p.symbol, p.category);
      if (cur == null) return;
      if (p.category !== "BIST" && rate == null) return;
      const mult = p.category === "BIST" ? 1 : rate;
      hasAny   = true;
      costTRY += p.amount * p.cost * mult;
      curTRY  += p.amount * cur  * mult;
    });
    if (!hasAny || costTRY === 0) return null;
    return { amt: curTRY - costTRY, pct: ((curTRY - costTRY) / costTRY) * 100 };
  }, [portfolio, getPrice, usdtry]);

  /* ── Shared styles ── */
  const currency = getCurrency(form.category);

  const inp = {
    padding: "11px 14px", borderRadius: 12,
    border: `1.5px solid ${t.border}`,
    background: t.inputBg, color: t.text,
    fontSize: 14, outline: "none",
    width: "100%", boxSizing: "border-box",
  };

  const cardStyle = {
    background: t.card, borderRadius: 20, padding: 24,
    border: `1px solid ${t.border}`,
    boxShadow: dark
      ? "0 4px 24px rgba(0,0,0,0.4)"
      : "0 4px 24px rgba(99,102,241,0.07)",
  };

  /* ── Render ── */
  return (
    <div style={{ minHeight: "100vh", background: t.bg, color: t.text, fontFamily: "'Inter', system-ui, sans-serif", display: "flex", flexDirection: "column" }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.startsWith("⚠️") ? "#f59e0b" : t.success,
          color: "#fff", padding: "12px 24px",
          borderRadius: 12, zIndex: 9999, fontWeight: 600,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}>
          {toast}
        </div>
      )}

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 24px", flex: 1, width: "100%", boxSizing: "border-box" }}>

        {/* ── Header ── */}
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 14,
              background: "linear-gradient(135deg, #6366f1, #06b6d4)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>🦉</div>
            <div>
              <h1 style={{
                margin: 0, fontSize: 24, fontWeight: 900, letterSpacing: -0.5,
                background: "linear-gradient(135deg, #6366f1, #06b6d4)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>OWL Portfolio</h1>
              <p style={{ margin: 0, color: t.muted, fontSize: 12 }}>Portföyünü takip et, büyüt.</p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => fetchPrices(portfolio)}
              disabled={pricesLoading || !portfolio.length}
              style={{
                padding: "9px 16px", borderRadius: 12,
                border: `1.5px solid ${t.border}`,
                background: t.card, color: pricesLoading ? t.muted : t.text,
                cursor: pricesLoading || !portfolio.length ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 7,
              }}
            >
              {pricesLoading ? "⏳" : "🔄"} {pricesLoading ? "Güncelleniyor…" : "Fiyatları Güncelle"}
            </button>
            <button
              onClick={() => setDark((d) => !d)}
              style={{
                padding: "9px 14px", borderRadius: 12,
                border: `1.5px solid ${t.border}`,
                background: t.card, color: t.text,
                cursor: "pointer", fontSize: 15,
              }}
            >
              {dark ? "☀️" : "🌙"}
            </button>
          </div>
        </header>

        {/* ── Tabs ── */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, alignItems: "center" }}>
          {[
            { id: "portfolio", label: "📊 Portföy"       },
            { id: "model",     label: "🎯 Model Portföy" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                padding: "10px 22px", borderRadius: 12,
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                background: activeTab === id
                  ? "linear-gradient(135deg, #6366f1, #818cf8)"
                  : t.card,
                color:  activeTab === id ? "#fff" : t.muted,
                boxShadow: activeTab === id ? "0 4px 16px rgba(99,102,241,0.35)" : "none",
                border: activeTab !== id ? `1.5px solid ${t.border}` : "none",
              }}
            >
              {label}
            </button>
          ))}
          {lastUpdated && (
            <span style={{ marginLeft: 10, fontSize: 12, color: t.muted }}>
              Son güncelleme: {lastUpdated.toLocaleTimeString("tr-TR")}
            </span>
          )}
        </div>

        {/* ══════════════ PORTFOLIO TAB ══════════════ */}
        {activeTab === "portfolio" && (
          <>
            {/* ── Add Asset Form ── */}
            <div style={{ ...cardStyle, marginBottom: 24 }}>
              <p style={{ margin: "0 0 14px", fontSize: 12, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: 1.5 }}>
                Varlık Ekle
              </p>
              <form onSubmit={addAsset} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>

                {/* Symbol + cross-category autocomplete */}
                <div style={{ position: "relative" }} ref={sugRef}>
                  <label style={labelSt(t)}>Sembol</label>
                  <input
                    style={inp}
                    placeholder="THYAO, NVDA, BTC, ALTIN…"
                    value={form.symbol}
                    onChange={(e) => onSymbolChange(e.target.value)}
                    onBlur={() => setTimeout(() => setShowSug(false), 150)}
                    onFocus={() => form.symbol && setShowSug(suggestions.length > 0)}
                    autoComplete="off"
                  />
                  {showSug && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 60,
                      background: t.card, border: `1px solid ${t.border}`,
                      borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                      overflow: "hidden",
                    }}>
                      {suggestions.map((s) => {
                        const cfg = CAT_CONFIG[s.category];
                        return (
                          <div
                            key={`${s.symbol}-${s.category}`}
                            onMouseDown={() => pickSuggestion(s.symbol, s.category)}
                            style={{
                              padding: "10px 14px", cursor: "pointer",
                              display: "flex", alignItems: "center", gap: 10,
                              borderBottom: `1px solid ${t.border}`,
                            }}
                          >
                            <span style={{
                              background: `${cfg.color}22`, color: cfg.color,
                              padding: "2px 8px", borderRadius: 20,
                              fontSize: 11, fontWeight: 700, flexShrink: 0,
                            }}>{s.category}</span>
                            <span style={{ fontWeight: 800, fontSize: 14 }}>{s.symbol}</span>
                            <span style={{ color: t.muted, fontSize: 13 }}>{s.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label style={labelSt(t)}>Adet</label>
                  <input
                    style={inp} type="number" placeholder="100" min="0"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>

                {/* Cost */}
                <div>
                  <label style={labelSt(t)}>Ort. Maliyet ({currency})</label>
                  <div style={{ position: "relative" }}>
                    <span style={{
                      position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                      color: t.muted, fontSize: 14, pointerEvents: "none", fontWeight: 600,
                    }}>{currency}</span>
                    <input
                      style={{ ...inp, paddingLeft: currency === "₺" ? 28 : 22 }}
                      type="number" step="any" placeholder="0.00"
                      value={form.cost}
                      onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                    />
                  </div>
                </div>

                <button type="submit" style={{
                  height: 46, padding: "0 24px", borderRadius: 12, border: "none",
                  background: "linear-gradient(135deg, #6366f1, #818cf8)",
                  color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14,
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                }}>
                  + Ekle
                </button>
              </form>
            </div>

            {/* ── Empty State ── */}
            {portfolio.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 20px", color: t.muted }}>
                <div style={{ fontSize: 72, marginBottom: 20 }}>📊</div>
                <h2 style={{ margin: "0 0 8px", color: t.text }}>Portföyünüz boş</h2>
                <p style={{ margin: 0 }}>Yukarıdaki formu kullanarak varlık ekleyin.</p>
              </div>
            ) : (
              <>
                {/* ── Total Value Banner ── */}
                <div style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
                  borderRadius: 20, padding: "22px 32px", marginBottom: 24,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  boxShadow: "0 8px 32px rgba(99,102,241,0.35)",
                }}>
                  <div>
                    <p style={{ margin: "0 0 4px", color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>
                      Toplam Portföy Değeri
                    </p>
                    {totalTRY != null ? (
                      <>
                        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 900, color: "#fff", letterSpacing: -1 }}>
                          {fmtTRY(totalTRY)} ₺
                        </h2>
                        <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                          1$ = ₺{fmtTRY(usdtry)}
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 900, color: "#fff", letterSpacing: -1 }}>
                          {fmtTRY(bistValue)} ₺
                        </h2>
                        {usdValue > 0 && (
                          <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                            + ${fmtUSD(usdValue)} (USD/TRY yükleniyor…)
                          </p>
                        )}
                      </>
                    )}
                    {portfolioPnl && (
                      <p style={{ margin: "6px 0 0", fontSize: 13, fontWeight: 700,
                        color: portfolioPnl.amt >= 0 ? "rgba(167,243,208,0.95)" : "rgba(252,165,165,0.95)" }}>
                        {portfolioPnl.amt >= 0 ? "▲ +" : "▼ "}₺{fmtTRY(Math.abs(portfolioPnl.amt))}
                        {" "}({portfolioPnl.pct >= 0 ? "+" : ""}{portfolioPnl.pct.toFixed(2)}%)
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ margin: "0 0 6px", color: "rgba(255,255,255,0.8)", fontSize: 14 }}>
                      {portfolio.length} varlık
                    </p>
                    <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: 14 }}>
                      {activeCategories.length} kategori
                    </p>
                  </div>
                </div>

                {/* ── Overview Pie ── */}
                <div style={{ ...cardStyle, marginBottom: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Genel Portföy Dağılımı</h2>
                      <p style={{ margin: "4px 0 0", color: t.muted, fontSize: 13 }}>
                        {usdtry ? "TL'ye çevrilmiş değerlere göre" : "Maliyet × Adet (giriş değeri)"}
                      </p>
                    </div>
                    <button
                      onClick={shareAllAssets}
                      style={{
                        display: "flex", alignItems: "center", gap: 7,
                        padding: "10px 20px", borderRadius: 12, border: "none",
                        background: "linear-gradient(135deg, #6366f1, #818cf8)",
                        color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14,
                        boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
                      }}
                    >
                      📤 Paylaş
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "center" }}>
                    <PieChartCard
                      data={categoryPie}
                      colors={categoryPie.map((c) => CAT_CONFIG[c.symbol]?.color || PALETTE[0])}
                      theme={t}
                      size={300}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {categoryPie.map((cat) => {
                        const cfg = CAT_CONFIG[cat.symbol] || { color: "#6366f1", icon: "📊" };
                        return (
                          <div key={cat.symbol} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "14px 18px", borderRadius: 14,
                            background: t.cardAlt, border: `1px solid ${t.border}`,
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <div style={{
                                width: 10, height: 10, borderRadius: "50%",
                                background: cfg.color, boxShadow: `0 0 8px ${cfg.color}80`,
                              }} />
                              <span style={{ fontWeight: 700, fontSize: 15 }}>
                                {cfg.icon} {cat.symbol}
                              </span>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{
                                fontSize: 18, fontWeight: 800,
                                background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}aa)`,
                                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                              }}>
                                %{cat.percent.toFixed(1)}
                              </div>
                              <div style={{ fontSize: 12, color: t.muted }}>
                                {cat.symbol === "BIST"
                                  ? `₺${fmtTRY(cat.value)}`
                                  : usdtry
                                    ? `₺${fmtTRY(cat.value)}`
                                    : `$${fmtUSD(cat.value / (usdtry || 1))}`
                                }
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Per-Category Sections ── */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: activeCategories.length === 1 ? "1fr" : "repeat(auto-fit, minmax(520px, 1fr))",
                  gap: 20,
                }}>
                  {activeCategories.map((cat) => {
                    const cfg       = CAT_CONFIG[cat];
                    const catData   = perCategoryPie[cat];
                    const catAssets = portfolio.filter((p) => p.category === cat);
                    const catTotal  = catAssets.reduce((s, p) => s + p.amount * p.cost, 0);
                    const catPct    = categoryPie.find((c) => c.symbol === cat)?.percent ?? 0;
                    const curr      = getCurrency(cat);

                    console.log({ cat, catData, catAssets, catTotal, catPct });

                    return (
                      <div key={cat} style={{
                        ...cardStyle, borderTop: `3px solid ${cfg.color}`, padding: 24,
                      }}>
                        {/* Category Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 20 }}>{cfg.icon}</span>
                              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: cfg.color }}>{cat}</h3>
                              <span style={{
                                padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                                background: `${cfg.color}22`, color: cfg.color,
                              }}>%{catPct.toFixed(1)} portföy</span>
                            </div>
                            <p style={{ margin: "5px 0 0 30px", color: t.muted, fontSize: 13 }}>
                              {curr}{cat === "BIST" ? fmtTRY(catTotal) : fmtUSD(catTotal)} · {catAssets.length} varlık
                            </p>
                          </div>
                          <button
                            onClick={() => shareAsImage(catData, PALETTE, `${cfg.icon} ${cat} Portföyüm`)}
                            style={{
                              padding: "8px 14px", borderRadius: 10,
                              border: `1.5px solid ${cfg.color}`,
                              background: "transparent", color: cfg.color,
                              fontWeight: 600, cursor: "pointer", fontSize: 13,
                              display: "flex", alignItems: "center", gap: 6,
                            }}
                          >
                            📤 Paylaş
                          </button>
                        </div>

                        {/* Pie + color legend row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
                          <div style={{ flexShrink: 0, width: 120 }}>
                            <PieChartCard
                              data={catData}
                              colors={PALETTE}
                              theme={t}
                              size={120}
                              compact
                            />
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {catData.map((d, i) => (
                              <span key={d.symbol} style={{
                                background: `${PALETTE[i % PALETTE.length]}22`,
                                color: PALETTE[i % PALETTE.length],
                                padding: "4px 10px", borderRadius: 20,
                                fontSize: 12, fontWeight: 700,
                              }}>
                                {d.symbol} %{d.percent.toFixed(0)}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Full-width table — no overflow */}
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr>
                              {["Sembol", "Adet", "Maliyet", "Güncel", "K / Z", "Pay", ""].map((h) => (
                                <th key={h} style={{
                                  textAlign: h === "Sembol" ? "left" : "right",
                                  padding: "6px 8px", color: t.muted, fontWeight: 700,
                                  fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8,
                                  borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {catAssets.map((p, i) => {
                               console.log({p, i});
                              const pct      = catData.find((x) => x.symbol === p.symbol)?.percent ?? 0;
                              const color    = PALETTE[i % PALETTE.length];
                              const curPrice = getPrice(p.symbol, p.category);
                              const pnlAmt   = curPrice != null ? (curPrice - p.cost) * p.amount : null;
                              const pnlPct   = curPrice != null ? ((curPrice - p.cost) / p.cost) * 100 : null;
                              const isProfit = pnlAmt != null && pnlAmt >= 0;

                              console.log({ symbol: p.symbol, curPrice, pnlAmt, pnlPct });

                              return (
                                <tr key={p.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                                  {/* Symbol */}
                                  <td style={{ padding: "10px 8px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                      <span style={{ fontWeight: 800 }}>{p.symbol}</span>
                                    </div>
                                  </td>
                                  {/* Amount */}
                                  <td style={{ padding: "10px 8px", textAlign: "right" }}>
                                    {p.amount.toLocaleString("tr-TR")}
                                  </td>
                                  {/* Cost */}
                                  <td style={{ padding: "10px 8px", textAlign: "right", color: t.muted }}>
                                    {curr}{cat === "BIST" ? fmtTRY(p.cost) : fmtUSD(p.cost)}
                                  </td>
                                  {/* Current Price */}
                                  <td style={{ padding: "10px 8px", textAlign: "right" }}>
                                    {curPrice != null
                                      ? <span style={{ fontWeight: 700 }}>{priceFmt(curPrice, cat)}</span>
                                      : <span style={{ color: t.muted, fontSize: 11 }}>—</span>
                                    }
                                  </td>
                                  {/* P&L */}
                                  <td style={{ padding: "10px 8px", textAlign: "right" }}>
                                    {pnlAmt != null ? (
                                      <div>
                                        <div style={{ fontWeight: 700, fontSize: 12, color: isProfit ? "#10b981" : "#ef4444" }}>
                                          {isProfit ? "+" : ""}
                                          {cat === "BIST" ? `₺${fmtTRY(pnlAmt)}` : `$${fmtUSD(Math.abs(pnlAmt))}`}
                                        </div>
                                        <div style={{ fontSize: 11, color: isProfit ? "#10b981" : "#ef4444" }}>
                                          {isProfit ? "▲" : "▼"}%{Math.abs(pnlPct).toFixed(1)}
                                        </div>
                                      </div>
                                    ) : (
                                      <span style={{ color: t.muted, fontSize: 11 }}>—</span>
                                    )}
                                  </td>
                                  {/* Share % */}
                                  <td style={{ padding: "10px 8px", textAlign: "right" }}>
                                    <span style={{
                                      background: `${color}22`, color,
                                      padding: "3px 8px", borderRadius: 20,
                                      fontSize: 12, fontWeight: 700,
                                    }}>
                                      %{pct.toFixed(1)}
                                    </span>
                                  </td>
                                  {/* Delete */}
                                  <td style={{ padding: "10px 6px" }}>
                                    <button
                                      onClick={() => removeAsset(p.id)}
                                      style={{
                                        background: "transparent", border: "none",
                                        color: t.danger, cursor: "pointer",
                                        padding: "2px 6px", borderRadius: 6, fontSize: 14, opacity: 0.7,
                                      }}
                                    >✕</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ══════════════ MODEL PORTFOLIO TAB ══════════════ */}
        {activeTab === "model" && (
          <ModelPortfolio t={t} />
        )}
      </div>

      {/* ── Disclaimer Footer ── */}
      <footer style={{
        borderTop: `1px solid ${t.border}`,
        background: t.card,
        padding: "16px 24px",
        marginTop: "auto",
      }}>
        <p style={{
          margin: 0, fontSize: 12, color: t.muted,
          maxWidth: 960, marginLeft: "auto", marginRight: "auto",
          textAlign: "center", lineHeight: 1.7,
        }}>
          ⚠️ <strong>Yasal Uyarı:</strong> Bu uygulama üzerindeki tüm paylaşımlar ve model portföy içerikleri yalnızca
          kişisel görüş ve bilgi amaçlı olup <strong>yatırım tavsiyesi niteliği taşımamaktadır.</strong>{" "}
          Herhangi bir yatırım kararı almadan önce lisanslı bir yatırım danışmanına başvurmanız tavsiye edilir.
          Geçmiş performans gelecekteki getiriyi garanti etmez. Fiyat verileri üçüncü taraf kaynaklardan
          alınmakta olup gecikmeli veya hatalı olabilir.
        </p>
      </footer>
    </div>
  );
}

const labelSt = (t) => ({
  display: "block", fontSize: 11, fontWeight: 700,
  color: t.muted, marginBottom: 6,
  textTransform: "uppercase", letterSpacing: 0.8,
});
